/**
 * The signed-in user's own profile (US #1814, Task #1820 A26; first/last name, Task #2415):
 * `GET` / `PATCH /api/profile`.
 *
 * One piece of state is SHARED across every caller: the last profile loaded or saved. The header
 * (`UserMenu.vue`) and the profile dialog (`UserProfileDialog.vue`) each call `useProfile()`, and a
 * Save in the dialog updates the header's name at once, with no reload and no second request.
 * The shared profile is tagged with the Auth0 subject it was loaded for, so a different user
 * signing in on the same tab never sees the previous user's name.
 *
 * Nothing here reads the `VITE_PUBLIC_REGISTRATION_ENABLED` switch: the two components gate every
 * call behind their own module-level const, so with the switch off this module is never called.
 *
 * @module composables/useProfile
 */

import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import type { UpdateProfileRequest, UserProfile } from '@/types/profile.types'
import { normalisePersonName } from '@/utils/display-name.utils'

export type ProfileResult<T> = { ok: true; value: T } | { ok: false; error: string }

export interface UseProfileReturn {
  /** The signed-in user's profile, once loaded or saved; null before that. */
  profile: ComputedRef<UserProfile | null>
  /**
   * The name the user saved in Site Compass (`"<first> <last>"` since Task #2415), or null when
   * they have not saved one. The header and the users page (Bug #2397) watch it.
   */
  savedDisplayName: ComputedRef<string | null>
  loading: Ref<boolean>
  saving: Ref<boolean>
  loadProfile: () => Promise<ProfileResult<UserProfile>>
  /**
   * Validates both names, then saves them (the server writes the joined display name too). An
   * invalid first or last name returns its message, first name first, without a request.
   */
  saveName: (firstName: string, lastName: string) => Promise<ProfileResult<UserProfile>>
}

/** The last profile loaded or saved, and whose it is. Shared by every `useProfile()` caller. */
const shared = ref<{ forSub: string; profile: UserProfile } | null>(null)

function apiUrl(): string {
  // VITE_BFF_URL already includes /api (the same base useUserManagement uses).
  return `${import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'}/profile`
}

async function errorFrom(response: Response, fallback: string): Promise<string> {
  if (response.status === 404) {
    return "Your profile isn't set up yet. Sign out, sign back in, then try again."
  }
  if (response.status === 429) {
    return 'Too many changes in a short time. Wait a minute, then try again.'
  }
  const body: unknown = await response.json().catch(() => null)
  const message =
    typeof body === 'object' && body !== null ? (body as Record<string, unknown>)['error'] : null
  return typeof message === 'string' && message.length > 0 ? message : fallback
}

export function useProfile(): UseProfileReturn {
  const authStore = useAuthStore()
  const loading = ref(false)
  const saving = ref(false)

  const profile = computed(() => {
    const current = shared.value
    const sub = authStore.user?.sub
    return current && sub && current.forSub === sub ? current.profile : null
  })
  const savedDisplayName = computed(() => profile.value?.displayName ?? null)

  /** Sends one request; on success stores the profile against the user it was sent for. */
  async function send(
    init: { method: 'GET' } | { method: 'PATCH'; body: string },
    fallback: string
  ): Promise<ProfileResult<UserProfile>> {
    const sub = authStore.user?.sub
    const token = authStore.isAuthenticated ? await authStore.getAccessToken() : null
    if (!sub || !token?.ok) return { ok: false, error: 'You are not signed in.' }

    try {
      const response = await fetch(apiUrl(), {
        ...init,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.value}` },
      })
      if (!response.ok) return { ok: false, error: await errorFrom(response, fallback) }
      const value = (await response.json()) as UserProfile
      shared.value = { forSub: sub, profile: value }
      return { ok: true, value }
    } catch {
      return { ok: false, error: `${fallback} Check your connection and try again.` }
    }
  }

  async function loadProfile(): Promise<ProfileResult<UserProfile>> {
    loading.value = true
    try {
      return await send({ method: 'GET' }, "Couldn't load your profile.")
    } finally {
      loading.value = false
    }
  }

  async function saveName(
    firstName: string,
    lastName: string
  ): Promise<ProfileResult<UserProfile>> {
    const name = normalisePersonName(firstName, lastName)
    if (!name.ok) return { ok: false, error: name.error }
    const request: UpdateProfileRequest = {
      firstName: name.value.firstName,
      lastName: name.value.lastName,
    }
    saving.value = true
    try {
      return await send(
        { method: 'PATCH', body: JSON.stringify(request) },
        "Couldn't save your name."
      )
    } finally {
      saving.value = false
    }
  }

  return { profile, savedDisplayName, loading, saving, loadProfile, saveName }
}
